import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, ImageOff, Info, Loader2, Star, X, XCircle } from "lucide-react";

// UI atoms compartilhados — botão, card, badge, avatar, estrelas, input, imagem,
// modal, aviso e esqueleto. Mantidos pequenos e sem lógica de negócio.
// Mudanças aqui afetam o app inteiro.
//
// Régua visual (plano de layout, 24/09/2026 — docs/PLANO_LAYOUT_PROFISSIONAL.md):
// laranja só na ação principal; cor de estado só no ícone/faixa, nunca no
// cartão inteiro; sem gradiente; todo clique que espera a API mostra spinner.

// `loading`: mostra spinner no lugar do ícone e trava o botão (evita duplo
// clique/POST duplicado). Mantém a largura pra tela não "pular".
export const Btn = ({ children, v = 'primary', sz = 'md', icon, onClick, disabled, full, loading = false, className = '', type = 'button' }) => {
  const vs = {
    primary:   'bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white shadow-sm shadow-orange-500/20',
    secondary: 'bg-[#12192a] hover:bg-slate-800 text-white',
    outline:   'border-2 border-orange-500 text-orange-600 hover:bg-orange-50',
    ghost:     'text-slate-500 hover:bg-slate-100',
    success:   'bg-emerald-600 hover:bg-emerald-700 text-white',
    white:     'bg-white hover:bg-slate-50 text-slate-700 shadow-sm border border-slate-200',
    mp:        'bg-[#009ee3] hover:bg-[#0087c4] text-white shadow-sm',
    danger:    'bg-red-500 hover:bg-red-600 text-white',
    // Ação destrutiva secundária (cancelar pedido/reserva): botão de verdade,
    // mas sem gritar — branco com borda e texto vermelho escuro.
    dangerOutline: 'bg-white hover:bg-red-50 text-red-700 border border-slate-200',
  };
  const szs = { xs: 'px-2.5 py-1 text-xs', sm: 'px-3.5 py-1.5 text-sm', md: 'px-5 py-2.5 text-sm', lg: 'px-7 py-3.5 text-base' };
  const spinner = { xs: 12, sm: 14, md: 16, lg: 18 }[sz] || 16;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`${vs[v] || vs.primary} ${szs[sz]} rounded-xl font-semibold transition-all inline-flex items-center gap-2 justify-center active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none ${full ? 'w-full' : ''} ${className}`}
    >
      {loading ? <Loader2 size={spinner} className="animate-spin flex-shrink-0" aria-label="Carregando" /> : icon}{children}
    </button>
  );
};

// ─── Modal único ────────────────────────────────────────────────
// Antes havia 62 overlays `fixed inset-0` feitos à mão com 8 tons de fundo
// diferentes. Um só: fundo black/50, cartão branco, cabeçalho com X, rodapé.
// `icone` (opcional) vai num círculo à esquerda do título; `tom` colore esse
// círculo (slate | orange | red | green | amber).
// z-[1000]: acima dos controles do mapa (Leaflet e o topo da rota usam 700) —
// o botão Voltar e o chip "Preparando" ficavam POR CIMA da vistoria (24/09).
export const Modal = ({ aberto = true, titulo, subtitulo, icone, tom = 'slate', onFechar, children, rodape, largura = 'max-w-sm', className = '', semPadding = false }) => {
  useEffect(() => {
    if (!aberto || !onFechar) return undefined;
    const aoTeclar = (e) => { if (e.key === 'Escape') onFechar(); };
    // Botão VOLTAR do Android (services/nativo.js) fecha o modal em vez de
    // sair da tela com ele aberto (auditoria 29/10, A8-13)
    const aoVoltar = (e) => { e.preventDefault(); onFechar(); };
    window.addEventListener('keydown', aoTeclar);
    window.addEventListener('toalugando:voltar', aoVoltar);
    return () => {
      window.removeEventListener('keydown', aoTeclar);
      window.removeEventListener('toalugando:voltar', aoVoltar);
    };
  }, [aberto, onFechar]);
  if (!aberto) return null;
  const tons = {
    slate: 'bg-slate-100 text-slate-600', orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600', green: 'bg-emerald-50 text-emerald-600', amber: 'bg-amber-50 text-amber-600',
  };
  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog" aria-modal="true" aria-label={typeof titulo === 'string' ? titulo : undefined}
      onClick={(e) => { if (e.target === e.currentTarget && onFechar) { onFechar(); repassarToqueParaBarra(e); } }}
    >
      <div className={`bg-white w-full ${largura} rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92dvh] flex flex-col animate-[toastIn_.2s_ease-out] ${className}`}>
        {(titulo || onFechar) && (
          <div className="px-5 pt-5 pb-3 flex items-start gap-3">
            {icone && (
              <span className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${tons[tom] || tons.slate}`}>{icone}</span>
            )}
            <div className="flex-1 min-w-0">
              {titulo && <h2 className="font-extrabold text-base text-[#12192a] leading-snug">{titulo}</h2>}
              {subtitulo && <p className="text-xs text-slate-500 mt-0.5">{subtitulo}</p>}
            </div>
            {onFechar && (
              <button type="button" onClick={onFechar} aria-label="Fechar"
                className="w-8 h-8 -mr-1 -mt-1 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 flex-shrink-0">
                <X size={18} />
              </button>
            )}
          </div>
        )}
        <div className={`flex-1 overflow-y-auto ${semPadding ? '' : 'px-5 pb-5'} ${titulo || onFechar ? '' : (semPadding ? '' : 'pt-5')}`}>{children}</div>
        {rodape && (
          <div className="px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 border-t border-slate-100 flex gap-2">{rodape}</div>
        )}
      </div>
    </div>
  );
};

// ─── Aviso inline ───────────────────────────────────────────────
// Substitui as caixas `bg-red-50 border` / `bg-emerald-50 border` (180+ no
// app): cartão branco, faixa fina de cor à esquerda e ícone do tipo.
const AVISO = {
  info:    { Icone: Info,          faixa: 'bg-slate-400',   cor: 'text-slate-600' },
  sucesso: { Icone: CheckCircle2,  faixa: 'bg-emerald-500', cor: 'text-emerald-600' },
  aviso:   { Icone: AlertTriangle, faixa: 'bg-amber-500',   cor: 'text-amber-600' },
  erro:    { Icone: XCircle,       faixa: 'bg-red-500',     cor: 'text-red-600' },
};
export const Aviso = ({ tipo = 'info', titulo, children, icone, acao, className = '' }) => {
  const e = AVISO[tipo] || AVISO.info;
  const Icone = e.Icone;
  return (
    <div role={tipo === 'erro' ? 'alert' : 'status'}
      className={`flex items-start gap-3 bg-white rounded-xl border border-slate-200 overflow-hidden ${className}`}>
      <span className={`w-1 self-stretch flex-shrink-0 ${e.faixa}`} />
      <span className={`mt-3 flex-shrink-0 ${e.cor}`}>{icone || <Icone size={16} />}</span>
      <div className="py-2.5 pr-3 min-w-0 flex-1 text-[12.5px] leading-snug text-slate-600">
        {titulo && <p className="font-bold text-[#12192a]">{titulo}</p>}
        {children}
        {acao && <div className="mt-2">{acao}</div>}
      </div>
    </div>
  );
};

// ─── Esqueleto de carregamento ──────────────────────────────────
// Nunca mais "Você não tem locações" antes dos dados chegarem: enquanto
// carrega, a tela mostra a silhueta do que vai aparecer.
export const Esq = ({ className = 'h-4 w-full' }) => (
  <div aria-hidden="true" className={`animate-pulse bg-slate-200/80 rounded-lg ${className}`} />
);
export const EsqueletoCard = ({ linhas = 2, comImagem = false }) => (
  <Card className="p-4">
    <div className="flex gap-3">
      {comImagem && <Esq className="w-16 h-16 rounded-xl flex-shrink-0" />}
      <div className="flex-1 space-y-2.5 py-0.5">
        <Esq className="h-4 w-2/3" />
        {Array.from({ length: linhas }).map((_, i) => <Esq key={i} className={`h-3 ${i % 2 ? 'w-1/2' : 'w-5/6'}`} />)}
      </div>
    </div>
  </Card>
);
export const EsqueletoLista = ({ n = 3, comImagem = false, className = '' }) => (
  <div className={`space-y-3 ${className}`} role="status" aria-label="Carregando">
    {Array.from({ length: n }).map((_, i) => <EsqueletoCard key={i} comImagem={comImagem} />)}
  </div>
);
// Tela inteira (fallback do Suspense enquanto o chunk da rota baixa).
export const EsqueletoTela = () => (
  <div className="min-h-screen bg-slate-50 px-4 pt-5" role="status" aria-label="Carregando">
    <Esq className="h-6 w-40 mb-4" />
    <EsqueletoLista n={4} comImagem />
  </div>
);

// `escuro`: cartão navy (#12192a) com texto branco — o destaque da verificação,
// o atalho do Portal de Gestão, o simulador do admin e o rodapé dos Termos.
// NÃO passe `bg-…` pela className pra escurecer um Card: no CSS gerado pelo
// Tailwind o `bg-white` da base vem DEPOIS de `bg-[#12192a]`/`bg-slate-800` e
// vence — o cartão ficava branco com texto branco (print do dono, 06/10/2026;
// regressão da Fase F, quando o gradiente virou cor sólida).
export const Card = ({ children, className = '', hover, onClick, id, escuro = false }) => (
  <div
    id={id}
    onClick={onClick}
    className={`${escuro ? 'bg-[#12192a] text-white border border-transparent' : 'bg-white border border-[#EBEEF2]'} rounded-[14px] shadow-sm ${hover ? 'hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer' : ''} ${className}`}
  >
    {children}
  </div>
);

export const Bdg = ({ children, c = 'slate', icon, sm }) => {
  const cs = {
    slate:  'bg-slate-100 text-slate-600',
    orange: 'bg-orange-100 text-orange-700',
    green:  'bg-emerald-50 text-emerald-700',
    blue:   'bg-blue-50 text-blue-700',
    red:    'bg-red-50 text-red-700',
    purple: 'bg-purple-50 text-purple-700',
    yellow: 'bg-amber-50 text-amber-700',
    grad:   'bg-orange-500 text-white',
    teal:   'bg-teal-50 text-teal-700',
    indigo: 'bg-indigo-50 text-indigo-700',
  };
  return (
    <span className={`${cs[c]} ${sm ? 'px-1.5 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'} rounded-full font-semibold inline-flex items-center gap-1`}>
      {icon}{children}
    </span>
  );
};

// Avatar com fallback automático: se a URL falhar (CORS do Google, 404, etc)
// cai pras iniciais. `referrerPolicy="no-referrer"` é crucial pra fotos do
// googleusercontent.com — sem isso o Google bloqueia hotlinking de outros domínios.
export const Avatar = ({ src, nome, fallback, className = '', textClass = 'text-base font-extrabold text-slate-500' }) => {
  const [erro, setErro] = useState(false);
  useEffect(() => { setErro(false); }, [src]);
  const iniciais = (nome || fallback || 'U').split(' ').map((n) => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  if (!src || erro) {
    return <span className={`${textClass} flex items-center justify-center w-full h-full`}>{iniciais}</span>;
  }
  return (
    <img
      src={src} alt={nome || 'avatar'} referrerPolicy="no-referrer"
      onError={() => setErro(true)}
      className={`w-full h-full object-cover ${className}`}
    />
  );
};

export const Stars = ({ r, n, s = 14 }) => {
  if (!r || r === 0 || n === 0) return <span className="text-xs text-slate-400 italic">Sem avaliações</span>;
  return (
    <div className="flex items-center gap-1">
      <Star size={s} className="fill-amber-400 text-amber-400" />
      <span className="font-bold text-slate-800 text-sm">{Number(r).toFixed(1)}</span>
      {n !== undefined && <span className="text-slate-400 text-xs">({n})</span>}
    </div>
  );
};

export const Input = ({ label, icon, textarea, error, required, ...p }) => {
  const borda = error ? 'border-red-400 bg-red-50' : 'border-slate-200';
  // text-base no mobile (16px) evita auto-zoom do iOS Safari ao focar.
  // text-sm volta no md+ pra densidade consistente do desktop.
  const baseFonte = 'text-base md:text-sm placeholder:text-slate-400';
  return (
    <div className="w-full">
      {label && (
        <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <div className="relative">
        {icon && <span className="absolute left-3.5 top-3 text-slate-400">{icon}</span>}
        {textarea
          ? <textarea {...p} className={`w-full ${icon ? 'pl-11' : 'pl-4'} pr-4 py-2.5 border ${borda} rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none ${baseFonte} resize-none`} rows={3} />
          : <input {...p} className={`w-full ${icon ? 'pl-11' : 'pl-4'} pr-4 py-2.5 border ${borda} rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none ${baseFonte}`} />}
      </div>
      {error && typeof error === 'string' && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
    </div>
  );
};

// Aceita emoji (string curta) ou URL. Detecta automático e renderiza
// como <img> ou <span> com o emoji em tamanho variável.
// Otimizações: lazy loading + decoding async + WebP via query params do
// provedor (Unsplash) + srcset 1x/2x pra retina.
import { urlOtimizada, srcSetOtimizado } from "../utils/imagens";
import { repassarToqueParaBarra } from "../lib/camadas";

export const ItemImage = ({ src, alt, className = "", size = "md", largura = 600 }) => {
  // Aceita http(s), data: URLs (base64 inline — vindas via integração Gestão)
  // e blob: URLs (uploads em andamento). Outras strings (ex: emoji) renderizam
  // como texto grande pra placeholder visual.
  const isUrl = src && (src.startsWith('http') || src.startsWith('https') || src.startsWith('data:') || src.startsWith('blob:'));
  const sizeClasses = {
    sm: "text-3xl",
    md: "text-4xl md:text-5xl",
    lg: "text-7xl",
    xl: "text-8xl",
  };
  // Sem foto: placeholder neutro em vez de área em branco (ou de imagem
  // genérica que confunde — "produto com foto errada")
  if (!src) {
    const iconSize = { sm: 22, md: 30, lg: 44, xl: 56 }[size] || 30;
    return (
      <div className={`flex flex-col items-center justify-center gap-1 bg-slate-100 text-slate-300 ${className}`}>
        <ImageOff size={iconSize} strokeWidth={1.5} />
        <span className="text-[9px] font-semibold text-slate-400">Sem foto</span>
      </div>
    );
  }
  if (isUrl) return <FotoSuave src={src} alt={alt} className={className} largura={largura} />;
  return <span className={`${sizeClasses[size]} drop-shadow-md ${className}`}>{src}</span>;
};

// Foto que entra com um esmaecimento curto quando termina de carregar, em vez
// de surgir de repente (dono, 16/10: "pisca e sobe"). Foto que já estava no
// cache aparece direto, sem esmaecer (conferido antes da 1ª pintura).
const FotoSuave = ({ src, alt, className, largura }) => {
  const ref = useRef(null);
  const [pronta, setPronta] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    setPronta(!!(el && el.complete && el.naturalWidth > 0));
  }, [src]);
  return (
    <img
      ref={ref}
      src={urlOtimizada(src, largura)}
      srcSet={srcSetOtimizado(src, largura)}
      alt={alt}
      loading="lazy"
      decoding="async"
      onLoad={() => setPronta(true)}
      onError={() => setPronta(true)}
      className={`object-contain transition-opacity duration-300 ease-out ${pronta ? 'opacity-100' : 'opacity-0'} ${className}`}
    />
  );
};

// Helper: formata número como BRL (R$ 1.234,56).
// Usa NON-BREAKING SPACE (U+00A0) entre "R$" e o numero pra evitar que o
// browser quebre o valor em duas linhas em containers estreitos (cards de
// pedido no mobile mostravam "R$" na primeira linha e "200" na segunda).
export const fmt = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
